import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import type { EditorActions } from '@atlaskit/editor-core';
import Link from '@atlaskit/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import {
	ConfigPanelFeedbackCollector,
	ConfigPanelGrouping,
	ExtensionsConfigWrapper,
	FeedbackType,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { useLinksMenuParametersAndActions } from '../../controllers/use-links-menu-parameters-and-actions';

import { ItemsTab } from './items-tab';
import { messages } from './messages';
import { StylesTab } from './styles-tab';

const SUPPORT_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/set-up-a-menu-of-important-links-on-company-hub/';

const stickyFooterStyles = xcss({
	paddingInline: 'space.050',
	paddingBlock: 'space.300',
	marginInline: 'space.negative.050',
	display: 'flex',
	flexDirection: 'column',
	position: 'sticky',
	bottom: 'space.0',
	backgroundColor: 'elevation.surface',
});

export type LinkMenuConfigPanelContentProps = {
	closeConfigPanel: () => void;
	editorActions: EditorActions;
	localId: string;
};

const LinksMenuFooter = () => (
	<Box xcss={stickyFooterStyles}>
		<ConfigPanelFeedbackCollector source="linksMenuExtension" type={FeedbackType.LINKS_MENU} />
	</Box>
);

export const LinksMenuConfigPanelContent = ({
	closeConfigPanel,
	localId,
	editorActions,
}: LinkMenuConfigPanelContentProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { showConfigPreambleSupportLink } = useProductDependencies();
	const { extensionParameters, extensionActions } = useLinksMenuParametersAndActions({
		localId,
		editorActions,
	});

	if (!extensionParameters) {
		return null;
	}

	const tabSections = [
		{
			label: formatMessage(messages.itemsLabel),
			value: 'items',
			tabContent: [
				{
					sectionId: 'main',
					component: (
						<ItemsTab
							extensionParameters={extensionParameters}
							extensionActions={extensionActions}
						/>
					),
				},
			],
		},
		{
			label: formatMessage(messages.stylesLabel),
			value: 'styles',
			tabContent: [
				{
					sectionId: 'general',
					component: <StylesTab parameters={extensionParameters} actions={extensionActions} />,
				},
			],
		},
	];

	return (
		<ExtensionsConfigWrapper
			title={<FormattedMessage {...messages.title} />}
			footerComponent={LinksMenuFooter}
			closeConfigPanel={closeConfigPanel}
		>
			<Text>
				<FormattedMessage
					{...(showConfigPreambleSupportLink
						? messages.preamble
						: messages.preambleWithoutSupportLink)}
					values={{
						linksMenuSupportLink: (
							<Link target="_blank" href={SUPPORT_LINK}>
								<FormattedMessage {...messages.preambleSupportLinkLabel} />
							</Link>
						),
					}}
				/>
			</Text>
			<ConfigPanelGrouping source="linksMenuExtensionConfig" tabSections={tabSections} />
		</ExtensionsConfigWrapper>
	);
};
